import { FC, useState } from "react";
import { useDispatch } from "react-redux";

import { Delete } from "@livechat/design-system-icons";
import { Button, Icon, Link, ModalPortal, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { buttonStyleWithTransition } from "routes/tickets/ticket/TicketAttachment";
import { ICannedResponseAttachment } from "store/entities/canned-responses";
import { SettingsViewActions } from "store/views/settings";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  cannedResponseId: string;
  attachment: ICannedResponseAttachment;
  attachmentIndex: number;
  allowDeletion: boolean;
}

const CannedAttachment: FC<IProps> = ({ attachment, cannedResponseId, attachmentIndex, allowDeletion }) => {
  const dispatch = useDispatch();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDeleteCannedResponseAttachment = (cannedResponseId: string, index: number) => () => {
    dispatch(SettingsViewActions.deleteCannedResponseAttachment(cannedResponseId, index));
    setDeleteModalOpen(false);
  };

  return (
    <li
      key={attachment.cid}
      css={{
        listStyle: "initial",
        marginLeft: withVar(SpacingToken.Spacing6),
      }}
    >
      <Flex alignItems="center">
        <Link target="_blank" href={attachment.url} bold={false} data-testid={`attachment-link-${attachment.name}`}>
          {attachment.name}
        </Link>
        {allowDeletion && (
          <Button
            icon={<Icon source={Delete} />}
            data-testid={`attachment-delete-${attachment.name}`}
            kind="plain"
            size="xcompact"
            onClick={() => setDeleteModalOpen(true)}
            css={buttonStyleWithTransition}
          />
        )}
      </Flex>
      {deleteModalOpen && (
        <ModalPortal zIndex={StackingContextLevel.Modal}>
          <ConfirmActionModal
            heading="Delete attachment?"
            onClose={() => {
              setDeleteModalOpen(false);
            }}
            data-testid="attachment-delete-modal"
            primaryAction={{
              label: "Delete attachment",
              actionType: "destructive",
              dataTestId: "canned-delete-attachment-modal-button",
              onClick: handleDeleteCannedResponseAttachment(cannedResponseId, attachmentIndex),
            }}
            secondaryAction={{
              label: "Cancel",
              onClick: () => setDeleteModalOpen(false),
            }}
          >
            If you delete this attachment from a canned response, it won&apos;t be displayed when sending new messages.
            However, the attachment in already sent messages will remain unaffected.
          </ConfirmActionModal>
        </ModalPortal>
      )}
    </li>
  );
};

export default CannedAttachment;
